import { useId, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Link2 } from "lucide-react";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { useCanEditAgent } from "~/pages/manifold-nextgen/hooks/useCanEditAgent";
import {
  Agent,
  AgentFeaturedConversation,
  AgentFeaturedConversationInput,
  FEATURED_CONVERSATION_LABEL_MAX_LENGTH,
  FEATURED_CONVERSATION_URL_MAX_LENGTH,
  MAX_FEATURED_CONVERSATIONS,
} from "~/services/agents";
import {
  AgentSortableListTab,
  EditFormFooter,
  type EditFormRenderProps,
} from "./AgentSortableListTab";

const toInput = (item: AgentFeaturedConversation): AgentFeaturedConversationInput => ({
  label: item.label,
  url: item.url,
});

const isValidUrl = (value: string): boolean => {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
};

const FeaturedConversationView = ({ item }: { item: AgentFeaturedConversation }) => (
  <div className="flex min-w-0 flex-col gap-0.5">
    <p className="truncate text-sm font-medium text-foreground">{item.label}</p>
    <div className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
      <Link2 className="size-3 shrink-0" />
      <span className="truncate">{item.url}</span>
    </div>
  </div>
);

const FeaturedConversationEditForm = ({
  initial,
  disabled,
  onSave,
  onCancel,
}: EditFormRenderProps<AgentFeaturedConversationInput>) => {
  const labelId = useId();
  const urlId = useId();
  const [label, setLabel] = useState(initial.label);
  const [url, setUrl] = useState(initial.url);

  const trimmedLabel = label.trim();
  const trimmedUrl = url.trim();
  const labelValid =
    trimmedLabel.length > 0 && trimmedLabel.length <= FEATURED_CONVERSATION_LABEL_MAX_LENGTH;
  const urlValid =
    trimmedUrl.length > 0 &&
    trimmedUrl.length <= FEATURED_CONVERSATION_URL_MAX_LENGTH &&
    isValidUrl(trimmedUrl);
  const canSave = labelValid && urlValid && !disabled;

  const handleSubmit = () => {
    if (!canSave) return;
    onSave({ label: trimmedLabel, url: trimmedUrl });
  };

  return (
    <>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={labelId}>Label</Label>
        <Input
          id={labelId}
          autoFocus
          placeholder="e.g. ERBB2 amplification cohort"
          maxLength={FEATURED_CONVERSATION_LABEL_MAX_LENGTH}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          disabled={disabled}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={urlId}>Shared conversation URL</Label>
        <Input
          id={urlId}
          placeholder="https://manifold.sc.manifold.ai/shared/conversations/…"
          maxLength={FEATURED_CONVERSATION_URL_MAX_LENGTH}
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          disabled={disabled}
        />
      </div>
      <EditFormFooter
        counterCurrent={label.length}
        counterMax={FEATURED_CONVERSATION_LABEL_MAX_LENGTH}
        canSave={canSave}
        disabled={disabled}
        onCancel={onCancel}
        onSave={handleSubmit}
      />
    </>
  );
};

export const AgentFeaturedConversationsTab = () => {
  const agent = useOutletContext<Agent>();
  const canEdit = useCanEditAgent(agent);

  return (
    <AgentSortableListTab<AgentFeaturedConversation, AgentFeaturedConversationInput>
      title="Featured Conversations"
      context="Pinned to the top of every user's conversation history"
      description={
        <>
          Pin up to {MAX_FEATURED_CONVERSATIONS} featured conversations that users will see pinned
          to the top of their conversation history. Drag to reorder. URLs must point to{" "}
          <code className="rounded bg-muted px-1 py-0.5 text-xs">/shared/conversations/…</code>.
        </>
      }
      agent={agent}
      field="featured_conversations"
      items={agent.featured_conversations}
      maxItems={MAX_FEATURED_CONVERSATIONS}
      canEdit={canEdit}
      toInput={toInput}
      emptyDraft={{ label: "", url: "" }}
      renderView={(item) => <FeaturedConversationView item={item} />}
      renderEditForm={(props) => <FeaturedConversationEditForm {...props} />}
      addLabel="Add featured conversation"
      emptyMessage="No featured conversations yet."
      errorMessage="Failed to save featured conversations"
    />
  );
};
